/**
 * Diagramas SVG compactos para el módulo de predimensionamiento.
 *
 * Cada diagrama se dimensiona de forma responsiva (width 100%, alto fijo)
 * y muestra la geometría preliminar con cotas. No son cálculos — solo
 * comunicación visual del resultado del predimensionamiento.
 */

import type { ReactNode } from 'react';

const HATCH_ID_CONCRETE = 'predim-hatch-concrete';
const HATCH_ID_BRICK = 'predim-hatch-brick';
const HATCH_ID_SOIL = 'predim-hatch-soil';

/** Shared definitions injected into each SVG. Safe to reference by id. */
function CommonDefs() {
  return (
    <defs>
      <pattern id={HATCH_ID_CONCRETE} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.22" strokeWidth="1" />
      </pattern>
      <pattern id={HATCH_ID_BRICK} patternUnits="userSpaceOnUse" width="14" height="8">
        <rect width="14" height="8" fill="#A16207" fillOpacity="0.45" />
        <line x1="0" y1="4" x2="14" y2="4" stroke="#451A03" strokeWidth="0.6" />
        <line x1="7" y1="0" x2="7" y2="4" stroke="#451A03" strokeWidth="0.6" />
        <line x1="0" y1="4" x2="0" y2="8" stroke="#451A03" strokeWidth="0.6" />
        <line x1="14" y1="4" x2="14" y2="8" stroke="#451A03" strokeWidth="0.6" />
      </pattern>
      <pattern id={HATCH_ID_SOIL} patternUnits="userSpaceOnUse" width="8" height="8">
        <rect width="8" height="8" fill="#7C2D12" fillOpacity="0.10" />
        <circle cx="2" cy="2" r="0.6" fill="#7C2D12" fillOpacity="0.4" />
        <circle cx="6" cy="6" r="0.6" fill="#7C2D12" fillOpacity="0.4" />
      </pattern>
    </defs>
  );
}

/* ====================================================================== */
/*  Diagram card wrapper                                                    */
/* ====================================================================== */

export function DiagramPanel({
  title,
  children,
  toolbar,
}: {
  title: string;
  children: ReactNode;
  toolbar?: ReactNode;
}) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-3"
      style={{ borderColor: 'var(--border-default)' }}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
          {title}
        </div>
        {toolbar}
      </div>
      <div className="flex justify-center">{children}</div>
    </div>
  );
}

/** Tiny toggle for 2D/3D used in predim DiagramPanels. */
export function View2D3DToggle({
  value, onChange,
}: { value: '2D' | '3D'; onChange: (v: '2D' | '3D') => void }) {
  return (
    <div className="flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
      {(['2D', '3D'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className="h-6 px-2.5 text-[10px] font-semibold tracking-[0.12em]"
          style={{
            background: value === m ? 'var(--accent)' : 'transparent',
            color: value === m ? 'var(--surface-base)' : 'var(--text-secondary)',
          }}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

/* ====================================================================== */
/*  COLUMN — cross-section                                                  */
/* ====================================================================== */

export function ColumnSectionDiagram({ b, h, cover = 4 }: { b: number; h: number; cover?: number }) {
  const W = 380;
  const H = 280;
  const PAD = 36;
  const maxDim = Math.max(b, h, 1);
  const scale = Math.min((W - 2 * PAD) / maxDim, (H - 2 * PAD - 30) / maxDim);
  const wpx = b * scale;
  const hpx = h * scale;
  const xL = (W - wpx) / 2;
  const yT = (H - hpx) / 2 - 5;
  const yB = yT + hpx;
  const xR = xL + wpx;

  // 4 corner bars + 2 intermediate per long side (typical for predim)
  const off = (cover + 1.5) * scale;
  const corners = [
    [xL + off, yT + off],
    [xR - off, yT + off],
    [xL + off, yB - off],
    [xR - off, yB - off],
  ];
  // Intermediate bars along the longer side
  const longSideIsH = h >= b;
  const intermediates: number[][] = [];
  if (longSideIsH) {
    intermediates.push([xL + off, (yT + yB) / 2]);
    intermediates.push([xR - off, (yT + yB) / 2]);
  } else {
    intermediates.push([(xL + xR) / 2, yT + off]);
    intermediates.push([(xL + xR) / 2, yB - off]);
  }

  // Stirrup outline
  const stirX = xL + (cover + 0.5) * scale;
  const stirY = yT + (cover + 0.5) * scale;
  const stirW = wpx - 2 * (cover + 0.5) * scale;
  const stirH = hpx - 2 * (cover + 0.5) * scale;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      <rect x={xL} y={yT} width={wpx} height={hpx}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.4" />
      <rect x={stirX} y={stirY} width={stirW} height={stirH}
        fill="none" stroke="#0891B2" strokeWidth="1.1" />
      {[...corners, ...intermediates].map((p, i) => (
        <circle key={`bar-${i}`} cx={p[0]} cy={p[1]} r={4}
          fill="var(--accent)" stroke="var(--concrete-outline)" strokeWidth="0.5" />
      ))}

      {/* Dimensions */}
      <text x={W / 2} y={yB + 18} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        b = {b} cm
      </text>
      <text x={xL - 8} y={(yT + yB) / 2} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}
        transform={`rotate(-90, ${xL - 8}, ${(yT + yB) / 2})`}>
        h = {h} cm
      </text>
    </svg>
  );
}

/* ====================================================================== */
/*  BEAM — cross-section + elevation                                        */
/* ====================================================================== */

export function BeamSectionDiagram({ b, h }: { b: number; h: number }) {
  const W = 240;
  const H = 220;
  const PAD = 24;
  const maxDim = Math.max(b, h, 1);
  const scale = Math.min((W - 2 * PAD) / maxDim, (H - 2 * PAD - 30) / maxDim);
  const wpx = b * scale;
  const hpx = h * scale;
  const xL = (W - wpx) / 2;
  const yT = (H - hpx) / 2 - 5;
  const yB = yT + hpx;
  const xR = xL + wpx;

  const off = 4 * scale;
  const top1 = [xL + off, yT + off];
  const top2 = [xR - off, yT + off];
  const bot1 = [xL + off, yB - off];
  const bot2 = [xR - off, yB - off];
  // Add a middle bottom bar if h > 50 (typical)
  const bot3 = [(xL + xR) / 2, yB - off];

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      <rect x={xL} y={yT} width={wpx} height={hpx}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.4" />
      {/* Stirrup */}
      <rect x={xL + 4} y={yT + 4} width={wpx - 8} height={hpx - 8}
        fill="none" stroke="#0891B2" strokeWidth="1.1" />
      {/* Bars */}
      {[top1, top2, bot1, bot2, bot3].map((p, i) => (
        <circle key={`bar-${i}`} cx={p[0]} cy={p[1]} r={3.5}
          fill="var(--accent)" stroke="var(--concrete-outline)" strokeWidth="0.5" />
      ))}
      <text x={W / 2} y={yB + 16} textAnchor="middle" fontSize="10.5" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        b = {b} cm
      </text>
      <text x={xL - 6} y={(yT + yB) / 2} textAnchor="middle" fontSize="10.5" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}
        transform={`rotate(-90, ${xL - 6}, ${(yT + yB) / 2})`}>
        h = {h} cm
      </text>
    </svg>
  );
}

export function BeamElevationDiagram({
  L, h, continuity,
}: {
  L: number; h: number;
  continuity: 'simple' | 'continuous' | 'cantilever';
}) {
  const W = 440;
  const H = 180;
  const PAD_X = 56;
  const PAD_T = 36;
  const beamW = W - 2 * PAD_X;
  const beamH = Math.min(60, h * (beamW / Math.max(L, 1)));
  const xL = PAD_X;
  const yT = PAD_T;
  const yB = yT + beamH;
  const xR = xL + beamW;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      {/* Beam */}
      <rect x={xL} y={yT} width={beamW} height={beamH}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.4" />
      {/* Top + bottom bars */}
      <line x1={xL + 4} y1={yT + 6} x2={xR - 4} y2={yT + 6} stroke="var(--accent)" strokeWidth="2" />
      <line x1={xL + 4} y1={yB - 6} x2={xR - 4} y2={yB - 6} stroke="var(--accent)" strokeWidth="2.4" />

      {/* Supports */}
      {continuity !== 'cantilever' && (
        <>
          <polygon points={`${xL},${yB + 1} ${xL - 10},${yB + 18} ${xL + 10},${yB + 18}`}
            fill="var(--text-muted)" opacity="0.75" />
          <polygon points={`${xR},${yB + 1} ${xR - 10},${yB + 18} ${xR + 10},${yB + 18}`}
            fill="var(--text-muted)" opacity="0.75" />
          <line x1={xL - 20} y1={yB + 18} x2={xL + 20} y2={yB + 18} stroke="var(--text-muted)" strokeWidth="1.2" />
          <line x1={xR - 20} y1={yB + 18} x2={xR + 20} y2={yB + 18} stroke="var(--text-muted)" strokeWidth="1.2" />
        </>
      )}
      {continuity === 'cantilever' && (
        <rect x={xL - 10} y={yT - 6} width="10" height={beamH + 12}
          fill="var(--text-muted)" opacity="0.5" />
      )}

      {/* Dimension */}
      <text x={(xL + xR) / 2} y={H - 10} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        L = {L} cm  ·  h = {h} cm
      </text>
      <text x={(xL + xR) / 2} y={yT - 8} textAnchor="middle" fontSize="9" fontFamily="monospace"
        fill="var(--text-muted)" style={{ fontStyle: 'italic' }}>
        {continuity === 'simple' ? 'simplemente apoyada'
          : continuity === 'cantilever' ? 'voladizo'
          : 'continua'}
      </text>
    </svg>
  );
}

/* ====================================================================== */
/*  SLAB — cross-section + plan                                             */
/* ====================================================================== */

export function SlabSectionDiagram({
  h, isRibbed, isTwoWay,
}: {
  h: number; isRibbed: boolean; isTwoWay: boolean;
}) {
  const W = 360;
  const H = 160;
  const PAD = 24;
  const showW = 100;  // 1 m strip representative
  const scale = Math.min((W - 2 * PAD) / showW, (H - 2 * PAD - 30) / Math.max(h, 8));
  const wpx = showW * scale;
  const hpx = h * scale;
  const xL = (W - wpx) / 2;
  const yT = (H - hpx) / 2;
  const yB = yT + hpx;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      {isRibbed ? (
        // Show a thin top flange (5 cm) + 2 ribs separated by a brick
        <g>
          {/* Top flange */}
          <rect x={xL} y={yT} width={wpx} height={5 * scale}
            fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.2" />
          {/* Brick block (between ribs) */}
          <rect x={xL + 12 * scale} y={yT + 5 * scale} width={36 * scale} height={hpx - 5 * scale}
            fill={`url(#${HATCH_ID_BRICK})`} stroke="#451A03" strokeWidth="0.5" />
          <rect x={xL + 60 * scale} y={yT + 5 * scale} width={36 * scale} height={hpx - 5 * scale}
            fill={`url(#${HATCH_ID_BRICK})`} stroke="#451A03" strokeWidth="0.5" />
          {/* Ribs (3) */}
          <rect x={xL} y={yT} width={12 * scale} height={hpx}
            fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.1" />
          <rect x={xL + 48 * scale} y={yT} width={12 * scale} height={hpx}
            fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.1" />
          <rect x={xL + 96 * scale} y={yT} width={4 * scale} height={hpx}
            fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.1" />
        </g>
      ) : (
        // Solid slab
        <rect x={xL} y={yT} width={wpx} height={hpx}
          fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.4" />
      )}
      <text x={W / 2} y={yB + 18} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        h = {h} cm  ·  {isTwoWay ? 'dos direcciones' : 'una dirección'}
        {isRibbed ? ' · aligerada' : ' · maciza'}
      </text>
    </svg>
  );
}

export function SlabPlanDiagram({
  L, Lb, isTwoWay,
}: {
  L: number; Lb?: number; isTwoWay: boolean;
}) {
  const W = 360;
  const H = 220;
  const PAD = 30;
  const Ly = isTwoWay && Lb ? Lb : L * 0.6;
  const max = Math.max(L, Ly, 1);
  const scale = Math.min((W - 2 * PAD) / max, (H - 2 * PAD - 40) / max);
  const wpx = L * scale;
  const hpx = Ly * scale;
  const xL = (W - wpx) / 2;
  const yT = 22;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      <rect x={xL} y={yT} width={wpx} height={hpx}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.4" />
      {/* Edge beams */}
      {[
        [xL, yT, xL + wpx, yT],
        [xL, yT + hpx, xL + wpx, yT + hpx],
        [xL, yT, xL, yT + hpx],
        [xL + wpx, yT, xL + wpx, yT + hpx],
      ].map((e, i) => (
        <line key={i} x1={e[0]} y1={e[1]} x2={e[2]} y2={e[3]}
          stroke="var(--text-secondary)" strokeWidth="3.5" opacity="0.7" />
      ))}

      {/* Direction arrows */}
      {!isTwoWay && (
        <g transform={`translate(${xL + wpx / 2}, ${yT + hpx / 2})`}>
          <line x1="-26" y1="0" x2="26" y2="0" stroke="var(--accent)" strokeWidth="1.5" />
          <polygon points="26,0 20,-4 20,4" fill="var(--accent)" />
          <polygon points="-26,0 -20,-4 -20,4" fill="var(--accent)" />
          <text x="0" y="-6" textAnchor="middle" fontSize="9" fontFamily="monospace"
            fill="var(--accent)" style={{ fontWeight: 700 }}>
            dirección de armado
          </text>
        </g>
      )}
      {isTwoWay && (
        <>
          {/* Bars in both directions */}
          {[0.2, 0.4, 0.6, 0.8].map((f, i) => (
            <line key={`v-${i}`}
              x1={xL + f * wpx} y1={yT + 5}
              x2={xL + f * wpx} y2={yT + hpx - 5}
              stroke="var(--accent)" strokeWidth="0.8" />
          ))}
          {[0.2, 0.4, 0.6, 0.8].map((f, i) => (
            <line key={`h-${i}`}
              x1={xL + 5} y1={yT + f * hpx}
              x2={xL + wpx - 5} y2={yT + f * hpx}
              stroke="var(--brand-violet, #8B5CF6)" strokeWidth="0.8" strokeDasharray="2 2" />
          ))}
        </>
      )}

      {/* Dimensions */}
      <text x={xL + wpx / 2} y={yT + hpx + 18} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        {isTwoWay ? `Lb = ${Lb} cm` : `L = ${L} cm (luz)`}
      </text>
      <text x={xL - 6} y={yT + hpx / 2} textAnchor="middle" fontSize="10.5" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}
        transform={`rotate(-90, ${xL - 6}, ${yT + hpx / 2})`}>
        {isTwoWay ? `La = ${L} cm` : 'transversal'}
      </text>
    </svg>
  );
}

/* ====================================================================== */
/*  WALL — elevation + cross-section                                        */
/* ====================================================================== */

export function WallElevationDiagram({ hw, lw, tw }: { hw: number; lw: number; tw: number }) {
  const W = 440;
  const H = 260;
  const PAD = 36;
  const maxDim = Math.max(hw, lw, 1);
  const scale = Math.min((W - 2 * PAD) / maxDim, (H - 2 * PAD - 40) / maxDim);
  const wpx = lw * scale;
  const hpx = hw * scale;
  const xL = (W - wpx) / 2;
  const yT = 24;
  const yB = yT + hpx;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      <rect x={xL} y={yT} width={wpx} height={hpx}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.4" />
      {/* Boundary elements (shaded amber strips at each end) */}
      <rect x={xL} y={yT} width={Math.min(40, wpx * 0.18)} height={hpx}
        fill="rgba(245,158,11,0.18)" stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 3" />
      <rect x={xL + wpx - Math.min(40, wpx * 0.18)} y={yT} width={Math.min(40, wpx * 0.18)} height={hpx}
        fill="rgba(245,158,11,0.18)" stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 3" />

      {/* Floor/ceiling slabs */}
      <line x1={xL - 14} y1={yT - 4} x2={xL + wpx + 14} y2={yT - 4} stroke="var(--text-secondary)" strokeWidth="4" opacity="0.55" />
      <line x1={xL - 14} y1={yB + 4} x2={xL + wpx + 14} y2={yB + 4} stroke="var(--text-secondary)" strokeWidth="4" opacity="0.55" />

      {/* Dimensions */}
      <text x={xL + wpx / 2} y={yB + 22} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        lw = {lw} cm
      </text>
      <text x={xL - 14} y={yT + hpx / 2} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--text-secondary)" style={{ fontWeight: 600 }}
        transform={`rotate(-90, ${xL - 14}, ${yT + hpx / 2})`}>
        hw = {hw} cm
      </text>
      <text x={xL + wpx / 2} y={yT - 9} textAnchor="middle" fontSize="9" fontFamily="monospace"
        fill="var(--text-muted)" style={{ fontStyle: 'italic' }}>
        tw = {tw} cm (espesor)
      </text>
    </svg>
  );
}

/* ====================================================================== */
/*  FOOTING — plan + elevation per variant                                  */
/* ====================================================================== */

export function FootingDiagram({
  variant, Lx, Ly, Hf, bc, hc, df = 120,
  L_cols, Lx2, Ly2,
}: {
  variant: 'isolated' | 'strip' | 'combined' | 'connected';
  Lx: number; Ly: number; Hf: number;
  bc?: number; hc?: number; df?: number;
  L_cols?: number; Lx2?: number; Ly2?: number;
}) {
  if (variant === 'isolated') {
    return <FootingIsolatedDiag Lx={Lx} Ly={Ly} Hf={Hf} bc={bc ?? 30} hc={hc ?? 30} df={df} />;
  }
  if (variant === 'strip') {
    return <FootingStripDiag B={Ly} Hf={Hf} tw={bc ?? 15} df={df} />;
  }
  if (variant === 'combined') {
    return <FootingCombinedDiag L={Lx} B={Ly} Hf={Hf} bc={bc ?? 30} hc={hc ?? 30} L_cols={L_cols ?? 400} df={df} />;
  }
  return <FootingConnectedDiag
    Lx1={Lx} Ly1={Ly} Hf={Hf}
    Lx2={Lx2 ?? Lx} Ly2={Ly2 ?? Ly}
    bc={bc ?? 30} L_cols={L_cols ?? 400} df={df} />;
}

function FootingIsolatedDiag({ Lx, Ly, Hf, bc, hc, df }: { Lx: number; Ly: number; Hf: number; bc: number; hc: number; df: number }) {
  // Two side-by-side mini-diagrams: plan + elevation
  const W = 600;
  const H = 200;
  // Plan area: left half
  const planW = W * 0.45;
  const elevW = W * 0.45;
  const gap = 16;
  const planPAD = 24;
  const elevPAD = 24;
  const planMax = Math.max(Lx, Ly, 1);
  const planScale = Math.min((planW - 2 * planPAD) / planMax, (H - 2 * planPAD - 20) / planMax);
  const pwx = Lx * planScale;
  const phy = Ly * planScale;
  const planX = (planW - pwx) / 2;
  const planY = (H - phy) / 2;

  const elevX0 = planW + gap;
  const elevMax = Math.max(Lx, df, 1);
  const elevScale = Math.min((elevW - 2 * elevPAD) / Lx, (H - 2 * elevPAD - 20) / Math.max(df, 1));
  const ewx = Lx * elevScale;
  const ePadY = 20;
  const grade_y = ePadY;
  const slabTop_y = grade_y + Math.max(df - Hf, 0) * elevScale;
  const slabBot_y = slabTop_y + Hf * elevScale;
  const elevSlabX = elevX0 + (elevW - ewx) / 2;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      {/* PLAN */}
      <text x={planW / 2} y={12} textAnchor="middle" fontSize="9.5" fontFamily="monospace"
        fill="var(--text-muted)" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
        Planta
      </text>
      <rect x={planX} y={planY} width={pwx} height={phy}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.4" />
      {/* Column centered */}
      <rect x={planX + (pwx - bc * planScale) / 2} y={planY + (phy - hc * planScale) / 2}
        width={bc * planScale} height={hc * planScale}
        fill="rgba(245,158,11,0.20)" stroke="var(--accent)" strokeWidth="1.4" />
      <text x={planX + pwx / 2} y={planY + phy + 14} textAnchor="middle" fontSize="10"
        fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        {Lx} × {Ly} cm
      </text>

      {/* ELEVATION */}
      <text x={elevX0 + elevW / 2} y={12} textAnchor="middle" fontSize="9.5" fontFamily="monospace"
        fill="var(--text-muted)" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
        Elevación
      </text>
      <rect x={elevX0 + elevPAD} y={grade_y} width={elevW - 2 * elevPAD}
        height={Math.max(slabBot_y - grade_y, 0)} fill={`url(#${HATCH_ID_SOIL})`} />
      <line x1={elevX0 + elevPAD} y1={grade_y} x2={elevX0 + elevW - elevPAD} y2={grade_y}
        stroke="#7C2D12" strokeWidth="1.3" />
      <rect x={elevSlabX} y={slabTop_y} width={ewx} height={Hf * elevScale}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.3" />
      {/* Column stub above grade */}
      <rect x={elevSlabX + (ewx - bc * elevScale) / 2} y={grade_y - 40}
        width={bc * elevScale} height={slabTop_y - (grade_y - 40)}
        fill="rgba(148,163,184,0.20)" stroke="var(--concrete-outline)" strokeWidth="1.2" />
      <text x={elevX0 + elevW / 2} y={slabBot_y + 14} textAnchor="middle" fontSize="10"
        fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        Hf = {Hf} cm  ·  df = {df} cm
      </text>
    </svg>
  );
}

function FootingStripDiag({ B, Hf, tw, df }: { B: number; Hf: number; tw: number; df: number }) {
  const W = 600;
  const H = 200;
  // Show cross-section (section perpendicular to wall) + a short plan
  const W_section = W * 0.5;
  const W_plan = W * 0.5;
  const PAD = 24;

  // Section
  const max_section = Math.max(B, df, 1);
  const scaleSec = Math.min((W_section - 2 * PAD) / B, (H - 2 * PAD - 20) / Math.max(df, 1));
  const Bpx = B * scaleSec;
  const xL_sec = (W_section - Bpx) / 2;
  const yGrade = 22;
  const ySlabTop = yGrade + Math.max(df - Hf, 0) * scaleSec;
  const ySlabBot = ySlabTop + Hf * scaleSec;
  const twpx = tw * scaleSec;

  // Plan
  const L_show = Math.max(150, 4 * tw);
  const planMax = Math.max(L_show, B, 1);
  const scalePlan = Math.min((W_plan - 2 * PAD) / planMax, (H - 2 * PAD - 20) / planMax);
  const planX0 = W_section + PAD;
  const Lpx = L_show * scalePlan;
  const Bpx2 = B * scalePlan;
  const planL = planX0 + (W_plan - 2 * PAD - Lpx) / 2;
  const planT = (H - Bpx2) / 2;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      <text x={W_section / 2} y={12} textAnchor="middle" fontSize="9.5" fontFamily="monospace"
        fill="var(--text-muted)" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
        Sección
      </text>
      <rect x={xL_sec - 30} y={yGrade} width={Bpx + 60}
        height={Math.max(ySlabBot - yGrade, 0)} fill={`url(#${HATCH_ID_SOIL})`} />
      <line x1={xL_sec - 30} y1={yGrade} x2={xL_sec + Bpx + 30} y2={yGrade}
        stroke="#7C2D12" strokeWidth="1.3" />
      <rect x={xL_sec} y={ySlabTop} width={Bpx} height={Hf * scaleSec}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.3" />
      {/* Wall above */}
      <rect x={xL_sec + (Bpx - twpx) / 2} y={Math.max(yGrade - 50, 0)} width={twpx}
        height={ySlabTop - Math.max(yGrade - 50, 0)} fill={`url(#${HATCH_ID_BRICK})`} stroke="#451A03" strokeWidth="1.2" />
      <text x={W_section / 2} y={H - 8} textAnchor="middle" fontSize="10"
        fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        B = {B} cm · Hf = {Hf} cm
      </text>

      {/* PLAN — strip continues left/right */}
      <text x={planX0 + (W_plan - 2 * PAD) / 2} y={12} textAnchor="middle" fontSize="9.5" fontFamily="monospace"
        fill="var(--text-muted)" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
        Planta (tramo)
      </text>
      <rect x={planL} y={planT} width={Lpx} height={Bpx2}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.3" />
      <rect x={planL} y={planT + (Bpx2 - tw * scalePlan) / 2} width={Lpx} height={tw * scalePlan}
        fill={`url(#${HATCH_ID_BRICK})`} stroke="#451A03" strokeWidth="1.1" />
      {/* Continuity arrows */}
      <line x1={planL - 14} y1={planT + Bpx2 / 2} x2={planL - 4} y2={planT + Bpx2 / 2}
        stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="2 2" />
      <line x1={planL + Lpx + 4} y1={planT + Bpx2 / 2} x2={planL + Lpx + 14} y2={planT + Bpx2 / 2}
        stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="2 2" />
      <text x={planL + Lpx / 2} y={planT + Bpx2 + 14} textAnchor="middle" fontSize="10"
        fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        tw = {tw} cm
      </text>
    </svg>
  );
}

function FootingCombinedDiag({ L, B, Hf, bc, hc, L_cols, df }: { L: number; B: number; Hf: number; bc: number; hc: number; L_cols: number; df: number }) {
  void hc;
  const W = 640;
  const H = 220;
  const W_plan = W * 0.5;
  const W_elev = W * 0.5;
  const PAD = 22;

  // Plan
  const planScale = Math.min((W_plan - 2 * PAD) / L, (H - 2 * PAD - 20) / B);
  const Lpx = L * planScale;
  const Bpx = B * planScale;
  const planX0 = (W_plan - Lpx) / 2;
  const planY0 = (H - Bpx) / 2;
  // Column positions: assume centered with L_cols spacing
  const c1x = planX0 + (L / 2 - L_cols / 2) * planScale;
  const c2x = planX0 + (L / 2 + L_cols / 2) * planScale;
  const cy = planY0 + Bpx / 2;
  const bcPx = bc * planScale;

  // Elevation
  const elevX0 = W_plan;
  const elevScale = Math.min((W_elev - 2 * PAD) / L, (H - 2 * PAD - 20) / Math.max(df + Hf, 1));
  const Lpx2 = L * elevScale;
  const HfPx = Hf * elevScale;
  const yGrade = 30;
  const ySlabTop = yGrade + Math.max(df - Hf, 0) * elevScale;
  const ySlabBot = ySlabTop + HfPx;
  const elevSlabX = elevX0 + (W_elev - Lpx2) / 2;
  const elevC1x = elevSlabX + (L / 2 - L_cols / 2) * elevScale;
  const elevC2x = elevSlabX + (L / 2 + L_cols / 2) * elevScale;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      <text x={W_plan / 2} y={14} textAnchor="middle" fontSize="9.5" fontFamily="monospace"
        fill="var(--text-muted)" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
        Planta
      </text>
      <rect x={planX0} y={planY0} width={Lpx} height={Bpx}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.3" />
      {/* 2 columns */}
      <rect x={c1x - bcPx / 2} y={cy - bcPx / 2} width={bcPx} height={bcPx}
        fill="rgba(245,158,11,0.20)" stroke="var(--accent)" strokeWidth="1.3" />
      <rect x={c2x - bcPx / 2} y={cy - bcPx / 2} width={bcPx} height={bcPx}
        fill="rgba(245,158,11,0.20)" stroke="var(--accent)" strokeWidth="1.3" />
      <text x={planX0 + Lpx / 2} y={planY0 + Bpx + 14} textAnchor="middle" fontSize="10"
        fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        L × B = {L} × {B}
      </text>

      <text x={elevX0 + W_elev / 2} y={14} textAnchor="middle" fontSize="9.5" fontFamily="monospace"
        fill="var(--text-muted)" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
        Elevación
      </text>
      <rect x={elevSlabX - 30} y={yGrade} width={Lpx2 + 60}
        height={Math.max(ySlabBot - yGrade, 0)} fill={`url(#${HATCH_ID_SOIL})`} />
      <line x1={elevSlabX - 30} y1={yGrade} x2={elevSlabX + Lpx2 + 30} y2={yGrade}
        stroke="#7C2D12" strokeWidth="1.3" />
      <rect x={elevSlabX} y={ySlabTop} width={Lpx2} height={HfPx}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.3" />
      {/* 2 columns */}
      {[elevC1x, elevC2x].map((x, i) => (
        <rect key={i} x={x - bcPx / 2} y={yGrade - 40} width={bcPx} height={ySlabTop - (yGrade - 40)}
          fill="rgba(148,163,184,0.20)" stroke="var(--concrete-outline)" strokeWidth="1.1" />
      ))}
      <text x={elevX0 + W_elev / 2} y={H - 8} textAnchor="middle" fontSize="10"
        fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        Hf = {Hf} cm · L_cols = {L_cols} cm
      </text>
    </svg>
  );
}

function FootingConnectedDiag({ Lx1, Ly1, Hf, Lx2, Ly2, bc, L_cols, df }: { Lx1: number; Ly1: number; Hf: number; Lx2: number; Ly2: number; bc: number; L_cols: number; df: number }) {
  void Ly2;
  void Ly1;
  const W = 640;
  const H = 220;
  const PAD = 22;
  const totalSpan = bc + L_cols + Lx2 / 2 + Lx1 / 2;   // approximate
  const scale = Math.min((W - 2 * PAD) / totalSpan, (H - 2 * PAD - 30) / Math.max(df + Hf, 1));
  const xL = PAD;
  const yGrade = 50;
  const ySlabTop = yGrade + Math.max(df - Hf, 0) * scale;
  const ySlabBot = ySlabTop + Hf * scale;

  const xCol1 = xL + (Lx1 / 4) * scale;   // medianera column near property line
  const xCol2 = xL + (Lx1 + L_cols) * scale;
  const f1x1 = xL;
  const f1x2 = xL + Lx1 * scale;
  const f2x1 = xCol2 - (Lx2 / 2) * scale;
  const f2x2 = xCol2 + (Lx2 / 2) * scale;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <CommonDefs />
      {/* Soil */}
      <rect x={xL - 20} y={yGrade} width={f2x2 - xL + 40}
        height={Math.max(ySlabBot - yGrade, 0)} fill={`url(#${HATCH_ID_SOIL})`} />
      <line x1={xL - 20} y1={yGrade} x2={f2x2 + 20} y2={yGrade}
        stroke="#7C2D12" strokeWidth="1.3" />
      {/* Property line */}
      <line x1={xL} y1={20} x2={xL} y2={H - 16}
        stroke="var(--danger)" strokeWidth="1.4" strokeDasharray="6 4" />
      <text x={xL - 6} y={30} textAnchor="end" fontSize="9" fontFamily="monospace"
        fill="var(--danger)" style={{ fontWeight: 700 }}>
        Lindero
      </text>
      {/* Footing 1 (medianera) */}
      <rect x={f1x1} y={ySlabTop} width={f1x2 - f1x1} height={Hf * scale}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.3" />
      <text x={(f1x1 + f1x2) / 2} y={ySlabBot + 12} textAnchor="middle" fontSize="9.5"
        fontFamily="monospace" fill="var(--text-muted)">
        Z1 (Lx1={Lx1})
      </text>
      {/* Footing 2 (interior) */}
      <rect x={f2x1} y={ySlabTop} width={f2x2 - f2x1} height={Hf * scale}
        fill={`url(#${HATCH_ID_CONCRETE})`} stroke="var(--concrete-outline)" strokeWidth="1.3" />
      <text x={(f2x1 + f2x2) / 2} y={ySlabBot + 12} textAnchor="middle" fontSize="9.5"
        fontFamily="monospace" fill="var(--text-muted)">
        Z2 (Lx2={Lx2})
      </text>
      {/* Columns */}
      <rect x={xCol1 - bc * scale / 2} y={yGrade - 50} width={bc * scale} height={ySlabTop - (yGrade - 50)}
        fill="rgba(220,38,38,0.20)" stroke="var(--danger)" strokeWidth="1.2" />
      <text x={xCol1} y={yGrade - 54} textAnchor="middle" fontSize="9"
        fontFamily="monospace" fill="var(--danger)" style={{ fontWeight: 700 }}>C medianera</text>
      <rect x={xCol2 - bc * scale / 2} y={yGrade - 50} width={bc * scale} height={ySlabTop - (yGrade - 50)}
        fill="rgba(34,197,94,0.20)" stroke="var(--success)" strokeWidth="1.2" />
      <text x={xCol2} y={yGrade - 54} textAnchor="middle" fontSize="9"
        fontFamily="monospace" fill="var(--success)" style={{ fontWeight: 700 }}>C interior</text>
      {/* Connection beam */}
      <rect x={xCol1 + bc * scale / 2} y={yGrade}
        width={xCol2 - xCol1 - bc * scale} height={20}
        fill="rgba(245,158,11,0.30)" stroke="var(--accent)" strokeWidth="1.2" />
      <text x={(xCol1 + xCol2) / 2} y={yGrade + 14} textAnchor="middle" fontSize="9"
        fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700 }}>
        viga conexión
      </text>
      <text x={(f1x1 + f2x2) / 2} y={H - 4} textAnchor="middle" fontSize="10"
        fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        Hf = {Hf} cm · L_cols = {L_cols} cm
      </text>
    </svg>
  );
}
